/**
 * ReaderReviewsSection — reviews from other readers, adapted from the same
 * visual DNA as profile/ScribbleCard.jsx (avatar + Stars + pulled quote +
 * quiet like/reply footer), plus a small composer so a reader can add their
 * own — persisted via useBookCommunityData's submitReview.
 */

import React, { useState } from 'react';
import { Star, Heart, MessageCircle, Trash2 } from 'lucide-react';
import { Stars, Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';

const TONE_BG = { coral: 'bg-coral', sage: 'bg-sage', yellow: 'bg-yellow', blue: 'bg-blue' };

function timeAgo(ts) {
  const days = Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function ReviewCard({ review, onDelete }) {
  return (
    <article className="flex gap-4 border-b border-line py-6 first:pt-0 last:border-b-0">
      <span
        className={cx(
          'grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-semibold text-ink/80',
          TONE_BG[review.tone] ?? TONE_BG.coral
        )}
        aria-hidden="true"
      >
        {review.name?.[0]?.toUpperCase() ?? '?'}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="font-semibold text-ink">
            {review.name}
            {review.mine && <span className="ml-1 font-normal text-subtle">(you)</span>}
          </p>
          {review.rating != null && <Stars value={Math.round(review.rating)} size={12} />}
        </div>
        <blockquote className="mt-2 font-display text-base leading-snug text-ink text-balance">&ldquo;{review.quote}&rdquo;</blockquote>
        {review.body && <p className="mt-2 text-sm leading-relaxed text-subtle">{review.body}</p>}
        <div className="mt-3 flex items-center gap-4 text-xs text-subtle">
          <span>{timeAgo(review.createdAt)}</span>
          <span className="inline-flex items-center gap-1.5"><Heart className="h-3.5 w-3.5" /> {review.likes}</span>
          <span className="inline-flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" /> Reply</span>
          {review.mine && (
            <button
              type="button"
              onClick={() => onDelete(review)}
              aria-label="Delete your review"
              title="Delete your review"
              className="inline-flex items-center text-subtle transition-colors hover:text-coral"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} aria-label={`${n} star${n > 1 ? 's' : ''}`}>
          <Star className={cx('h-5 w-5', n <= value ? 'fill-yellow text-yellow' : 'text-line')} />
        </button>
      ))}
    </div>
  );
}

function ReviewComposer({ myReview, onSubmit }) {
  const [rating, setRating] = useState(myReview?.rating ?? 0);
  const [quote, setQuote] = useState(myReview?.quote ?? '');
  const [body, setBody] = useState(myReview?.body ?? '');
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!quote.trim() || !body.trim() || !rating || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await onSubmit({ rating, quote: quote.trim(), body: body.trim() });
      setSaved(true);
    } catch (submitError) {
      setError(submitError.message || 'We could not save your review.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card-paper mb-2 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-subtle">
        {myReview ? 'Your review' : 'Add your review'}
      </p>
      <div className="mt-2"><StarPicker value={rating} onChange={(n) => { setRating(n); setSaved(false); }} /></div>
      <textarea
        value={quote}
        onChange={(e) => { setQuote(e.target.value); setSaved(false); }}
        placeholder="What did you think?"
        rows={2}
        data-testid="book-details-review-input"
        className="mt-2 w-full resize-none rounded-xl border border-line bg-cream px-3.5 py-2.5 text-sm text-ink outline-none focus:border-coral"
      />
      <textarea
        value={body}
        onChange={(e) => { setBody(e.target.value); setSaved(false); }}
        placeholder="Tell other readers more about it..."
        rows={3}
        className="mt-2 w-full resize-none rounded-xl border border-line bg-cream px-3.5 py-2.5 text-sm text-ink outline-none focus:border-coral"
      />
      {error && <p className="mt-2 text-xs font-medium text-coral" role="alert">{error}</p>}
      <div className="mt-2 flex items-center justify-end gap-2">
        {saved && <span className="text-xs text-subtle">Saved</span>}
        <Button
          variant="primary"
          icon={false}
          onClick={submit}
          disabled={!quote.trim() || !body.trim() || !rating || submitting}
          className="px-4 py-1.5 text-xs"
          data-testid="book-details-review-submit"
        >
          {submitting ? 'Saving...' : myReview ? 'Update Review' : 'Post Review'}
        </Button>
      </div>
    </div>
  );
}

export default function ReaderReviewsSection({ reviews, totalReviews, hasMoreReviews, onLoadMore, myReview, onSubmitReview, onDeleteReview, reviewsError }) {
  const handleDelete = async (review) => {
    if (!window.confirm('Delete your review?')) return;
    try {
      await onDeleteReview(review.id);
      toast('Review deleted');
    } catch (error) {
      toast(error.message || 'We could not delete your review.');
    }
  };

  return (
    <section className="u-container mt-10 md:mt-14" data-testid="book-details-reviews">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-display text-lg font-semibold text-ink">Reader Reviews</h2>
        {totalReviews > 0 && <span className="text-xs text-subtle">{totalReviews.toLocaleString()} reviews</span>}
      </div>

      <div className="mt-4">
        <ReviewComposer myReview={myReview} onSubmit={onSubmitReview} />

        {reviewsError && <p className="mb-3 rounded-xl border border-coral/30 bg-coral/10 px-3 py-2 text-xs font-medium text-coral" role="alert">{reviewsError}</p>}

        {reviews.length === 0 ? (
          <p className="py-6 text-sm text-subtle">No reviews yet — be the first to share your thoughts.</p>
        ) : (
          reviews.map((review) => <ReviewCard key={review.id} review={review} onDelete={handleDelete} />)
        )}

        {hasMoreReviews && (
          <div className="pt-4 text-center">
            <Button variant="secondary" icon={false} onClick={onLoadMore} className="px-5 py-2 text-xs">
              Load more reviews
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
